import { ValuePerformanceBlock } from "components/PercentageBrick";

import { Box, Link, useTheme, Typography } from "@mui/material";
import { Col, Row } from "v2/components/base/Flex/ui";
import { CabalWithShills } from "v2/components/ScrollableItem/GroupRowItem/hocs/withGroupRowItem";
import Rank1Icon from "assets/icons/shiller-rank-1.png";
import Rank2Icon from "assets/icons/shiller-rank-2.png";
import Rank3Icon from "assets/icons/shiller-rank-3.png";
import { Value } from "components/Value";

const MAX_CALL_SHOW = 5;

const RankIconMap: Record<number, () => JSX.Element> = {
  1: () => <img src={Rank1Icon} alt="1" />,
  2: () => <img src={Rank2Icon} alt="2" />,
  3: () => <img src={Rank3Icon} alt="3" />,
};

const GroupRowItem = ({
  cabal,
  rank,
  navigateTo,
  handleOnError,
}: {
  cabal: CabalWithShills;
  rank: number;
  navigateTo: () => void;
  handleOnError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const {
    tgGroupId,
    imageUri,
    name,
    cabalCall = [],
    totalCalls = 0,
    change = 0,
    shills = 0,
  } = cabal;

  return (
    <>
      <div id={`cabalId-${tgGroupId}`} />
      <GroupRowContainer navigateTo={navigateTo}>
        <Row
          section="group-item-info"
          type="bc"
          sx={{
            gap: "12px",
            maxWidth: "60%",
          }}>
          <Row
            section="group-image"
            type="ss"
            sx={{
              position: "relative",
              paddingY: "4px",
              width: "34px",
            }}>
            <GroupImage src={imageUri ?? ""} alt={name} onError={handleOnError} />
            {rank <= 3 ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  transform: "translate(50%, -4px)",
                }}>
                {RankIconMap[rank]()}
              </Box>
            ) : (
              <></>
            )}
          </Row>
          <Col
            section="group-item-info-name"
            type="sc"
            sx={{
              maxWidth: "100%",
              gap: "4px",
              overflow: "hidden",
            }}>
            <Row
              section="group-item-info-name"
              type="sc"
              sx={{
                gap: "6px",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginTop: "-4px",
              }}>
              <Typography
                variant="subtitle"
                sx={{
                  maxWidth: "100%",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}>
                {name}
              </Typography>
            </Row>
            {cabalCall.length ? (
              <Row section="group-item-info-calls" type="cs" sx={{ marginRight: "auto" }}>
                {cabalCall
                  .filter((call) => {
                    const xPerformance = call.latestPriceUsd / call.priceUsd;
                    const THRESHOLD = 0.01;
                    return !(xPerformance < 1 + THRESHOLD || call.marketCap <= 0);
                  })
                  .map((call, index) => {
                    return (
                      <Box
                        sx={{
                          width: "16px",
                          height: "16px",
                          transform: `translateX(-${50 * index + 0}%)`,
                          border: "1px solid #000000",
                          boxSizing: "border-box",
                          borderRadius: "100%",
                          background: "#FFFFFF",
                          marginTop: "-4px",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                        <img src={call.iconUri} alt="1" width={"100%"} height={"100%"} />
                      </Box>
                    );
                  })}
                {totalCalls > MAX_CALL_SHOW ? (
                  <Typography
                    sx={{
                      width: "16px",
                      height: "16px",
                      transform: `translateX(-${50 * 5 - 70}%)`,
                      marginTop: "-4px",
                    }}
                    variant="body-sm">
                    <b>+{totalCalls - MAX_CALL_SHOW}</b>
                  </Typography>
                ) : null}
              </Row>
            ) : null}
          </Col>
        </Row>
        <Col section="group-item-info-shills" type="ee">
          <Typography sx={{ whiteSpace: "nowrap" }} color="brand.primary" variant="subtitle">
            <Value color="brand.primary" variant="subtitle" value={shills} /> SHILLS
          </Typography>
          <ValuePerformanceBlock value={Math.round(change)} />
        </Col>
      </GroupRowContainer>
    </>
  );
};

const GroupRowContainer = ({
  children,
  navigateTo,
}: {
  children: React.ReactNode;
  navigateTo: () => void;
}) => {
  const theme = useTheme();
  return (
    // <Link onClick={navigateTo} sx={{ all: "unset", cursor: "pointer", maxWidth: "100%" }}>
    <Row
      type="bc"
      sx={{
        cursor: "pointer",
        padding: "20px 10px",
        border: `1px solid ${theme.palette.border.dark}`,
        background: theme.palette.surface.silver,
        borderRadius: theme.spacing(1.5),
        gap: "4px",
        maxWidth: "100%",
        minHeight: "80px",
      }}
      fullWidth
      onClick={navigateTo}>
      {children}
    </Row>
    // </Link>
  );
};

const GroupImage = ({
  src,
  alt,
  onError,
}: {
  src: string;
  alt: string;
  onError: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}) => {
  const theme = useTheme();

  return (
    <img
      style={{
        width: "34px",
        height: "34px",
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.border.dark}`,
        objectFit: "cover",
        objectPosition: "center",
        background: theme.palette.surface.container,
      }}
      src={src}
      alt={alt}
      onError={onError}
    />
  );
};

export default GroupRowItem;
